<template>
  <LayoutPage :title="pageTitle" :loading="loading">
    <div class="sticky-container pb-3">
      <div v-if="showRegionFacilitySelectBoxes">
        <RegionAndFacilitySelect over-view-page />
      </div>
    </div>
    <MonitorTileCards class="mt-2" />

    <v-row>
      <v-col>
        <v-card outlined flat tile class="black-border">
          <v-card-title class="title" style="font-weight: 500">
            Incident Disposition Report
          </v-card-title>
          <div class="d-flex justify-end">
            <template>
              <div class="mr-3">
                <DateTimeFilter
                  v-model="dateRange"
                  :ranges="['Today', '2d', '1w', '1m']"
                  default="1m"
                  custom
                  class="black-border"
                  time-zone="America/New_York"
                />
              </div>
              <IncidentFilter class="mr-4" @apply="applyFilter" />

              <div style="min-width: 240px" class="mr-2">
                <SearchField v-model="search" />
              </div>
            </template>
          </div>
          <v-skeleton-loader
            :loading="loading"
            type="table-thead, table-row-divider@7"
            class="mx-3"
          >
            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="incidents"
                :custom-filter="customFilter"
                :custom-sort="customSort"
                :search="search"
                :sort-by.sync="tableState.sortBy"
                :sort-desc.sync="tableState.sortDesc"
                :page.sync="tableState.page"
                :items-per-page.sync="tableState.itemsPerPage"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                class="custom-table elevation-1 custom-pagination"
              >
                <template #item="{ item }">
                  <tr>
                    <td>
                      <ButtonView title="View" @click="gotoZone(item)" />
                    </td>
                    <td style="min-width: 120px">{{ item.shortId }}</td>
                    <td style="min-width: 120px">
                      {{ toUpperCase(item.shortCode) }}
                    </td>
                    <td style="min-width: 200px">
                      <div>
                        <strong>Facility</strong> : {{ item.incidentLocation }}
                      </div>
                      <div class="mt-2">
                        <strong>Smart Unit / Zone</strong> : {{ item.zoneName }}
                      </div>
                    </td>
                    <td>{{ item.incidentType }}</td>
                    <td>
                      <div>
                        {{ titleCase(item.incidentDisposition) }}
                        <div
                          v-if="item.isOverdue"
                          class="overdue-text mt-1 justify-center"
                        >
                          Overdue!
                        </div>
                      </div>
                    </td>
                    <td>
                      <SeverityChip :severity="titleCase(item.priority)" />
                    </td>
                    <td>{{ item.responder }}</td>
                    <td>{{ formatDateTime4Humans(item.startedAt) }}</td>
                    <td>{{ item.dueAt }}</td>
                    <td>{{ item.statusText }}</td>
                  </tr>
                </template>

                <template #footer.prepend>
                  <ButtonExportCSV
                    class="order-last"
                    :table-data="incidents"
                    :headers="headers"
                    filename="Incident Disposition Report"
                  />
                </template>
              </v-data-table>
              <div class="d-flex justify-center my-4 custom-pagination-arrow">
                <v-btn
                  class="ml-4"
                  icon
                  :disabled="!paginationTracking.hasMore.newer"
                  @click="loadNextIncidents"
                  :loading="isLoading"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  icon
                  :disabled="!paginationTracking.hasMore.older"
                  @click="loadPreviousIncidents"
                  :loading="isLoading"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-skeleton-loader>
        </v-card>
      </v-col>
    </v-row>
  </LayoutPage>
</template>

<script>
import { ref } from "vue";
import { useRouter } from "vue-router/composables";
import useUser from "@components/authentication/useUser";
import RegionAndFacilitySelect from "../RegionAndFacilitySelect.vue";
import LayoutPage from "@layout/LayoutPage.vue";
import useDashboard from "../../dashboard/useDashboard";
import useDashboardTitle from "../useDashboardTitle";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import SearchField from "@atoms/SearchField.vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import ButtonView from "@atoms/ButtonView.vue";

import { INCIDENT_HISTORY } from "../../../components/reports/graphql";
import useDataTable from "../../common/useDataTable";
import {
  incidentStatus,
  formatIncidentType,
} from "../../common/helpers/incident";
import { formatPhoneNumber } from "@tod-ui/helpers/phone";
import { diff, durationInWords } from "@tod-ui/helpers/datetime";
import { stringSortCollator } from "@tod-ui/helpers/strings";
import useFacility from "../../authentication/useFacility";
import { titleCase, toUpperCase } from "@tod-ui/helpers/strings";
import IncidentFilter from "../../reports/IncidentFilter.vue";
import SeverityChip from "@tod-ui/components/SeverityChip.vue";
import MonitorTileCards from "./MonitorTileCards.vue";
export default {
  name: "PageMonitorAndControlHub",
  components: {
    RegionAndFacilitySelect,
    LayoutPage,
    DateTimeFilter,
    SearchField,
    ButtonExportCSV,
    ButtonView,
    SeverityChip,
    IncidentFilter,
    MonitorTileCards,
  },
  setup() {
    const router = useRouter();
    const { defaultOrganization } = useUser();
    const pageTitle = useDashboardTitle("Monitoring & Control Hub Dashboard");
    const { organizationsLoading, selectedFacilities, facilityIds } =
      useDashboard();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["startedAt"],
      sortDesc: [true],
    });
    const {
      currentFacilityId,
      facilityFeatureEnabled,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      facilityFormatDateTime,
    } = useFacility();
    const isLoading = ref(false);

    return {
      isLoading,
      defaultOrganization,
      pageTitle,
      organizationsLoading,
      selectedFacilities,
      facilityIds,
      currentFacilityId,
      facilityFeatureEnabled,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      facilityFormatDateTime,
      tableState,
      showAllItems,
      restoreItemsPerPage,
    };
  },
  data() {
    return {
      showRegionFacilitySelectBoxes: true,
      search: null,
      dateRange: { after: undefined },
      filter: {},
      incidentInfo: {},
      headers: [
        { text: "Details", value: "details" },
        { text: "Incident ID", value: "shortId" },
        { text: "Incident Code", value: "shortCode" },
        { text: "Incident Location", value: "task_location" },
        { text: "Incident Type", value: "incident_type" },
        { text: "Incident Disposition", value: "incident_disposition" },
        { text: "Priority", value: "severity" },
        { text: "Renter Details", value: "renter_details" },
        { text: "Started On", value: "started_on" },
        { text: "Target Close Date", value: "target_close_date" },
        { text: "Status", value: "status" },
      ],
      paginationTracking: {
        minSeqId: null,
        maxSeqId: null,
        hasMore: {
          newer: false,
          older: false,
        },
        historyStack: [],
      },
    };
  },
  computed: {
    loading() {
      const { incidents } = this.$apollo.queries;
      return incidents.loading || this.isLoading;
    },
  },

  apollo: {
    incidents: {
      query: INCIDENT_HISTORY,
      variables() {
        return {
          // Scoped by the selected facilities alone. Sending a single
          // `parentId` too would AND the two, dropping every facility outside
          // the default organization's subtree.
          dateTimeFilter: this.dateRange,
          dispositions: ["open", "in_progress", "in_review"],
          priorities: ["critical", "severe", "moderate", "substantial", "low"],
          parentIds: this.facilityIds,
          limit:
            this.tableState.itemsPerPage > 0
              ? this.tableState.itemsPerPage
              : null,
          seqIdLt: this.paginationTracking.seqIdLt,
          ...this.filter,
        };
      },
      skip() {
        // Without a facility list this query has no organization scope at all,
        // so it must not run until one is loaded.
        return !this.dateRange?.after || !this.facilityIds.length;
      },
      update({ incidents }) {
        const processedData = incidents
          .filter((incident) => {
            const url = new URL(window.location.href);
            const searchParams = url.searchParams;
            const status = searchParams.get("status");
            if (status && status === this.selectedDisposition) {
              this.selectedDisposition = status;
            }

            // return incident.organization.ancestors.some((ancestor) =>
            //   this.selectedFacilities.some(
            //     (facility) => facility.id === ancestor.id
            //   )
            // );
            return true;
          })
          .map((incident) => {
            const responder = incident.responders?.length
              ? incident.responders.sort(
                  (a, b) => a.escalationLevel - b.escalationLevel
                )[0]
              : "--";

            const endedAt =
              incident.lastUntriggeredAt ||
              incident.closedAt ||
              incident.resolvedAt;
            const isOverdue = incident.tasks?.some(
              (task) =>
                new Date(task.dueAt) < new Date() &&
                task.resolution !== "completed"
            );

            return {
              ...incident,
              shortId: incident.shortId,
              shortCode: incident.shortCode,
              incidentLocation: incident.alerting_strategy.facility_org.name,
              zoneName: incident.organization.name,
              incidentType: formatIncidentType(incident),
              incidentDisposition: incident.disposition,
              priority: incident.priority,
              responder: this.formatResponder(responder),
              startedAt: incident.startedAt,
              endedAt,
              duration: diff(incident.startedAt, endedAt).as("minutes"),
              statusText: incidentStatus(incident),
              dueAt:
                incident.tasks?.length > 0
                  ? this.facilityFormatDateTime4Humans(incident.tasks[0]?.dueAt)
                  : "NA",
              isOverdue,
            };
          });
        if (processedData.length > 0) {
          const sortedBySeqId = [...processedData].sort(
            (a, b) => a.seqId - b.seqId
          );
          this.paginationTracking.minSeqId = sortedBySeqId[0].seqId;
          this.paginationTracking.maxSeqId =
            sortedBySeqId[sortedBySeqId.length - 1].seqId;
          this.paginationTracking.hasMore.older =
            processedData.length >= this.tableState.itemsPerPage;
          this.paginationTracking.hasMore.newer =
            this.paginationTracking.historyStack.length > 0;
        }
        this.isLoading = false;
        return processedData;
      },
      error() {
        this.isLoading = false;
      },
    },
  },
  watch: {
    selectedFacilities() {
      this.resetPagination();
    },
    "tableState.itemsPerPage"() {
      this.resetPagination();
    },
    dateRange: {
      deep: true,
      handler() {
        this.resetPagination();
      },
    },
    filter: {
      deep: true,
      handler() {
        this.resetPagination();
      },
    },
  },
  mounted() {
    this.loadIncidentHistory();
  },
  methods: {
    gotoZone(incident) {
      this.incidentInfo.value = incident;
      this.$router.push({
        name: "PageIncidentInfo",
        params: {
          organization_id: incident.organization.ancestors[0].id,
          incident_id: incident.id,
        },
      });
    },
    formatDateTime4Humans(datetime) {
      return this.facilityFormatDateTime4Humans(datetime);
    },

    applyFilter(filter) {
      const _filter = { ...filter };

      _filter.organizationSubtypes = _filter.locations;
      delete _filter.locations;

      if (_filter.severityStatus?.length) {
        _filter.priorities = _filter.severityStatus;
      }

      delete _filter.severityStatus;

      if (_filter.overdueStatus?.length) {
        _filter.isOverdue = _filter.overdueStatus.includes("overdue");
      }
      delete _filter.overdueStatus;

      if (_filter.statuses?.length) {
        _filter.resolutions = [];

        if (_filter.statuses.includes("authorized")) {
          _filter.resolutions.push("claimed");
        }

        if (_filter.statuses.includes("unauthorized")) {
          _filter.resolutions.push("denied");
          _filter.resolutions.push("surveyed");
        }

        if (_filter.statuses.includes("unacknowledged")) {
          _filter.resolutions.push("unexplained");
        }
      }
      delete _filter.statuses;

      for (let k in _filter) {
        if (_filter[k] === null) {
          delete _filter[k];
        } else if (typeof _filter[k] === "boolean" && !_filter[k]) {
          delete _filter[k];
        } else if (Array.isArray(_filter[k]) && !_filter[k].length) {
          delete _filter[k];
        }
      }

      if (_filter.afterHours) {
        _filter["duringAccessHours"] =
          _filter.afterHours === "after-hours" ? false : undefined;
      }

      this.filter = { ..._filter };
    },
    customFilter(value, search, item) {
      if (!search) return true;
      const _search = search.toLowerCase();
      const match = [
        item.zoneName.toLowerCase(),
        item.incidentLocation.toLowerCase(),
        item.incidentType.toLowerCase(),
        item.incidentDisposition.toLowerCase(),
        item.shortId.toLowerCase(),
        item.priority.toLowerCase(),
        item.responder.toLowerCase(),
        this.facilityFormatDateTime4Humans(item.startedAt),
      ].join(" ");

      return match.indexOf(_search) >= 0;
    },
    customSort(items, sortBy, sortDesc) {
      const _sortBy = sortBy[0];
      const _sortDesc = sortDesc[0] ? -1 : 1;

      items.sort((a, b) => {
        if (_sortBy === "statusText") {
          const aStatus = `${a.status} ${a.resolution}`;
          const bStatus = `${b.status} ${b.resolution}`;

          return (bStatus < aStatus ? -1 : 1) * _sortDesc;
        } else {
          if (typeof a[_sortBy] === "string") {
            return (
              stringSortCollator.compare(a[_sortBy], b[_sortBy]) * _sortDesc
            );
          }
          return (b[_sortBy] < a[_sortBy] ? -1 : 1) * _sortDesc;
        }
      });

      return items;
    },
    formatResponder(responder) {
      if (!responder?.name) return "--";
      if (!responder?.contactMethod?.value) return responder.name;
      return `${responder.name} @ ${formatPhoneNumber(
        responder.contactMethod.value
      )}`;
    },

    loadNextIncidents() {
      if (this.paginationTracking.historyStack.length === 0) {
        return;
      }
      this.isLoading = true;
      const previousState = this.paginationTracking.historyStack.pop();
      this.paginationTracking.seqIdLt = previousState.seqIdLt;
      this.$nextTick(() => {
        this.$apollo.queries.incidents.refetch().catch((error) => {
          console.error("Error fetching previous page:", error);
          this.isLoading = false;
        });
      });
    },

    loadPreviousIncidents() {
      if (!this.paginationTracking.minSeqId) return;
      this.paginationTracking.historyStack.push({
        seqIdLt: this.paginationTracking.seqIdLt,
        minSeqId: this.paginationTracking.minSeqId,
        maxSeqId: this.paginationTracking.maxSeqId,
      });
      this.isLoading = true;
      this.paginationTracking.seqIdLt = this.paginationTracking.minSeqId;
      this.$nextTick(() => {
        this.$apollo.queries.incidents.refetch().catch((error) => {
          console.error("Error fetching older incidents:", error);
          this.isLoading = false;
        });
      });
    },
    resetPagination() {
      this.paginationTracking = {
        minSeqId: null,
        maxSeqId: null,
        seqIdLt: null,
        hasMore: {
          newer: false,
          older: false,
        },
        historyStack: [],
      };

      this.loadIncidentHistory();
    },
    loadIncidentHistory() {
      this.paginationTracking.seqIdLt = null;
      this.isLoading = true;
      this.$nextTick(() => {
        this.$apollo.queries.incidents
          .refetch()
          .then(() => {})
          .catch((error) => {
            console.error("Error fetching incidents:", error);
            this.isLoading = false;
          });
      });
    },
    titleCase,
    toUpperCase,
  },
};
</script>
<style>
.custom-pagination .v-data-footer__icons-before,
.custom-pagination .v-data-footer__icons-after {
  visibility: hidden !important;
}
.custom-pagination .v-data-footer__pagination {
  display: none !important;
}
.custom-pagination-arrow {
  position: absolute;
  bottom: 10px;
  right: 60px;
}
</style>
